import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, TrendingUp, TrendingDown, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

import ColorPicker from '@/components/ColorPicker';
import { HelpTip } from '@/components/HelpTip';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('saida');
  const [formData, setFormData] = useState({
    name: '',
    color: '#6366F1',
    type: 'saida',
    exclude_from_operational: false,
  });

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, formData);
        toast.success('Categoria atualizada!');
      } else {
        await api.post('/categories', formData);
        toast.success('Categoria criada!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadCategories();
    } catch (error) {
      toast.error('Erro ao salvar categoria');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir esta categoria?')) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Categoria excluida!');
      loadCategories();
    } catch (error) {
      toast.error('Erro ao excluir categoria');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', color: '#6366F1', type: activeTab, exclude_from_operational: false });
    setEditingCategory(null);
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, color: category.color, type: category.type || 'saida', exclude_from_operational: category.exclude_from_operational || false });
    setIsDialogOpen(true);
  };

  const filteredCategories = categories.filter((c) => (c.type || 'saida') === activeTab);

  return (
    <div data-testid="categories-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Categorias
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Organize suas transações em categorias por tipo
          <HelpTip text="Categorias ajudam a organizar suas entradas e saídas. Cada transação pertence a uma categoria, facilitando a análise dos seus gastos e receitas." className="ml-1" />
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'entrada' ? 'default' : 'outline'}
            onClick={() => setActiveTab('entrada')}
            data-testid="tab-entrada"
            className={activeTab === 'entrada' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <TrendingUp size={18} className="mr-2" />
            Entradas
          </Button>
          <Button
            variant={activeTab === 'saida' ? 'default' : 'outline'}
            onClick={() => setActiveTab('saida')}
            data-testid="tab-saida"
            className={activeTab === 'saida' ? 'bg-rose-600 hover:bg-rose-700' : ''}
          >
            <TrendingDown size={18} className="mr-2" />
            Saídas
          </Button>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-category-button" className="bg-indigo-600 hover:bg-indigo-700 ml-auto">
              <Plus size={18} className="mr-2" />
              Nova Categoria
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="category-dialog">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Editar' : 'Nova'} Categoria</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Tipo</Label>
                <Select value={formData.type} onValueChange={(val) => setFormData({ ...formData, type: val })}>
                  <SelectTrigger data-testid="category-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entrada">Entrada</SelectItem>
                    <SelectItem value="saida">Saída</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nome</Label>
                <Input
                  type="text"
                  data-testid="category-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Fornecedor, Ads, Saque Shopee"
                />
              </div>
              <div>
                <Label>Cor</Label>
                <ColorPicker value={formData.color} onChange={(c) => setFormData({ ...formData, color: c })} testIdPrefix="cat-color" />
              </div>
              {formData.type === 'saida' && (
                <div className="mt-2 flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border">
                  <input
                    type="checkbox"
                    id="exclude-operational"
                    data-testid="exclude-operational-checkbox"
                    checked={formData.exclude_from_operational}
                    onChange={(e) => setFormData({ ...formData, exclude_from_operational: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-border accent-violet-600 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <label htmlFor="exclude-operational" className="text-sm font-medium text-foreground cursor-pointer">
                      Retirada de lucros
                    </label>
                    <div className="flex items-start gap-1.5 mt-1">
                      <Info size={13} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Marque se esse gasto for um saque ou retirada pessoal, e não um custo do negócio. Isso ajuda a ver seu lucro real no Dashboard.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <Button type="submit" data-testid="category-submit-button" className="w-full bg-indigo-600 hover:bg-indigo-700">
                {editingCategory ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="categories-list">
        {loading ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">Carregando...</div>
        ) : filteredCategories.length === 0 ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">
            Nenhuma categoria de {activeTab === 'entrada' ? 'entrada' : 'saída'} encontrada
          </div>
        ) : (
          filteredCategories.map((category) => (
            <Card key={category.id} className="hover:shadow-md transition-shadow" data-testid={`category-card-${category.id}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: category.color }}
                  >
                    <div className="w-6 h-6 bg-white/30 rounded"></div>
                  </div>
                  <div>
                    <CardTitle className="text-lg">{category.name}</CardTitle>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        category.type === 'entrada'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-rose-100 text-rose-700'
                      }`}>
                        {category.type === 'entrada' ? 'Entrada' : 'Saída'}
                      </span>
                      {category.exclude_from_operational && category.type === 'saida' && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                          Retirada de lucros
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(category)}
                    data-testid={`edit-category-${category.id}`}
                    className="flex-1"
                  >
                    <Pencil size={16} className="mr-2" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(category.id)}
                    data-testid={`delete-category-${category.id}`}
                    className="flex-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Categories;
